import api from './api';

class DealsService {
  // Get all deals with filters and pagination
  async getDeals(params = {}) {
    try {
      const response = await api.get('/deals', { params });
      return response.data;
    } catch (error) {
      // Handle 501 (not implemented) by returning mock data
      if (error.response?.status === 501) {
        console.warn('Deals endpoint not implemented, returning mock data');
        return this.getMockDeals(params);
      }
      throw this.handleError(error);
    }
  }

  // Get deal by ID
  async getDeal(id) {
    try {
      const response = await api.get(`/deals/${id}`);
      return response.data.data.deal;
    } catch (error) {
      // Handle 501 (not implemented) by returning mock data
      if (error.response?.status === 501) {
        console.warn('Deal detail endpoint not implemented, returning mock data');
        return this.getMockDeal(id);
      }
      throw this.handleError(error);
    }
  }

  // Create new deal
  async createDeal(data) {
    try {
      const response = await api.post('/deals', data);
      return response.data.deal;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update deal
  async updateDeal(id, data) {
    try {
      const response = await api.put(`/deals/${id}`, data);
      return response.data.deal;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateDealStatus(id, is_active) {
    try {
      const response = await api.put(`/deals/${id}/toggle`, { is_active });
      return response.data.deal;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Delete deal
  async deleteDeal(id) {
    try {
      const response = await api.delete(`/deals/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Toggle deal status (active/inactive)
  async toggleDealStatus(id) {
    try {
      const response = await api.put(`/deals/${id}/toggle`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get deal analytics
  async getDealAnalytics(id, params = {}) {
    try {
      const response = await api.get(`/deals/${id}/analytics`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get deal usage statistics
  async getDealUsage(id, params = {}) {
    try {
      const response = await api.get(`/deals/${id}/usage`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Bulk operations
  async bulkUpdateDeals(data) {
    try {
      const response = await api.post('/deals/bulk', data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get deal types
  getDealTypes() {
    return [
      { value: 'percentage', label: 'Percentage Discount' },
      { value: 'fixed_amount', label: 'Fixed Amount Discount' }
    ];
  }

  // Get deal status options
  getStatusOptions() {
    return [
      { value: 'active', label: 'Active', color: '#4caf50' },
      { value: 'inactive', label: 'Inactive', color: '#f44336' },
      { value: 'scheduled', label: 'Scheduled', color: '#ff9800' },
      { value: 'expired', label: 'Expired', color: '#9e9e9e' },
    ];
  }

  // Mock data methods for when endpoints are not implemented
  getMockDeals(params = {}) {
    const mockDeals = [
      {
        id: 1,
        name: 'Early Bird Special',
        description: 'Book 3 months in advance and save 20%',
        type: 'percentage',
        value: 20,
        status: 'active',
        start_date: '2024-01-01',
        end_date: '2024-12-31',
        usage_count: 45,
        max_usage: 100,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-15T00:00:00Z'
      },
      {
        id: 2,
        name: 'Group Discount',
        description: 'Save RM 500 for bookings of 5 or more people',
        type: 'fixed',
        value: 500,
        status: 'active',
        start_date: '2024-01-01',
        end_date: '2024-12-31',
        usage_count: 12,
        max_usage: 50,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-10T00:00:00Z'
      },
      {
        id: 3,
        name: 'Ramadan Special',
        description: 'Special offer for Ramadan bookings',
        type: 'percentage',
        value: 15,
        status: 'scheduled',
        start_date: '2024-03-01',
        end_date: '2024-04-30',
        usage_count: 0,
        max_usage: 200,
        created_at: '2024-02-01T00:00:00Z',
        updated_at: '2024-02-01T00:00:00Z'
      }
    ];

    // Apply pagination
    const page = parseInt(params.page) || 1;
    const limit = parseInt(params.limit) || 20;
    const offset = (page - 1) * limit;

    // Apply filters
    let filteredDeals = mockDeals;
    if (params.status) {
      filteredDeals = filteredDeals.filter(deal => deal.status === params.status);
    }
    if (params.search) {
      const searchTerm = params.search.toLowerCase();
      filteredDeals = filteredDeals.filter(deal =>
        deal.name.toLowerCase().includes(searchTerm) ||
        deal.description.toLowerCase().includes(searchTerm)
      );
    }

    const total = filteredDeals.length;
    const deals = filteredDeals.slice(offset, offset + limit);

    return {
      deals,
      pagination: {
        page,
        limit,
        total,
        pages: Math.ceil(total / limit)
      }
    };
  }

  getMockDeal(id) {
    const mockDeals = this.getMockDeals().deals;
    const deal = mockDeals.find(d => d.id === parseInt(id));
    return deal || null;
  }

  // Handle API errors
  handleError(error) {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    } else if (error.message) {
      return new Error(error.message);
    } else {
      return new Error('An unexpected error occurred');
    }
  }
}

export default new DealsService();